var React = require('react');
var { createPortal } = require('react-dom');
var {
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  useMemo,
} = require('react');
var { useSelector, useDispatch } = require('react-redux');
var dayjs = require('../../utils/dayjsConfig');
var { withTranslation } = require('react-i18next');
var { useTranslation } = require('react-i18next');
var planningManager = require('@managers/planningManager');
var fieldManager = require('@managers/fieldManager');
var { sensorsMap } = require('sensorsManager');
var { NavigationBar } = require('../general/NavigationBar');
var { fertilizerNameToDisplay } = require('nitrogenManager');
const { ReportEventModal } = require('ReportEventModal');
const { unitDisplayName } = require('@managers/unitsManager');
const {
  pressureAlertLevel,
  pressureAlertHintKey,
} = require('@utils/pressureAlert');
var distributionActions = require('@actions/distributionActions');
var { DeleteModal } = require('../general/Modal/DeleteModal');
var {
  upper,
  calculateDischargeDeviation,
  DISCHARGE_ALERT_THRESHOLD,
  wtrMaterialConcentrationsDict,
} = require('helpers');
var { Filter } = require('Filter');
var { formatPressureSubtitle } = require('../general/eventPopUpHelpers');
var {
  getReportedTypeChips,
  getComingUpTypeChips,
  filterEventsByInactiveTypes,
  getInactiveTypesFromChips,
} = require('./eventsFilterHelpers');
const $ = require('jquery');
require('./AllFarmView/IrrigationSystemMonitoring.css');

const IMAGES_MAP = {
  irr: require('images/icons/planner/irrigation_icon.svg'),
  fert: require('images/icons/planner/fert_icon.svg'),
  precipitation: require('images/icons/planner/rain.svg'),
  plan_hcl: require('images/icons/planner/hcl_icon.svg'),
  plan_hpo: require('images/icons/planner/hpo_icon.svg'),
  plan_note: require('images/icons/planner/note_icon.svg'),
  discharge: require('images/icons/discharge_icon_.svg'),
  note: require('images/icons/planner/note_icon.svg'),
  wtr: require('images/icons/wtr_icon.svg'),
};

const choices = ['coming up', 'reported'];
const REPORTED_HISTORY_KEYS = [
  'wfr_length',
  'fert',
  'precipitation_daily',
  'discharge',
];

const hasReportedHistoryData = (historicalData) => {
  if (!historicalData) {
    return false;
  }

  return Object.keys(historicalData).some((property) =>
    REPORTED_HISTORY_KEYS.includes(property),
  );
};

const ComingUp = memo(({ plans, timezone }) => {
  const { t } = useTranslation();
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );
  let noPlannerEvents = (
    <div
      className='p-3 text-center w-100 h-100 text-size-18 font-weight-semi-bold d-flex align-items-center justify-content-center'
      style={{ whiteSpace: 'pre-line', color: '#A8ABB7' }}
    >
      {t('no_planner_events')}
    </div>
  );
  return (
    <div>
      {plans.length === 0 || !timezone
        ? noPlannerEvents
        : plans[plans.length - 1].startDate < dayjs().unix()
          ? noPlannerEvents
          : plans.map((item) => {
              let note = item?.note ? t(item.note) : '';
              return (
                <EventRow
                  key={item.planType + item.startDate}
                  type={item.planType}
                  startDate={item.startDate}
                  value={item.value}
                  extraData={{
                    endDate: item.endDate,
                    duration: item.duration,
                    fertType: item.fertType,
                    wtrType: item.type,
                    wtrConcentrations:
                      selectedFarmingUnit?.wt?.material_concentrations?.hasOwnProperty(
                        item?.type,
                      )
                        ? selectedFarmingUnit?.wt?.material_concentrations[
                            item?.type
                          ]
                        : wtrMaterialConcentrationsDict[item?.type],
                  }}
                  units={item.units}
                  timezone={timezone}
                  event={'coming_up'}
                  shouldDisplayHover={false}
                  note={note}
                />
              );
            })}
    </div>
  );
});

const ReportedEvents = memo(({ filteredReportedEvents, timezone }) => {
  const { t } = useTranslation();
  const [dischargeExtraData, setDischargeExtraData] = useState([]);
  const selectedField = useSelector(
    (state) => state.distribution_data.selected_entities.field,
  );
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );

  useEffect(() => {
    if (
      selectedField !== null &&
      selectedField?.historical_data?.hasOwnProperty('discharge')
    ) {
      let testsExtraData = calculateDischargeDeviation(
        {},
        selectedField?.historical_data?.discharge,
      );
      setDischargeExtraData(testsExtraData);
    }
  }, [filteredReportedEvents]);

  let noReportedEvents = (
    <div
      className='p-5 text-center w-100 h-100 text-size-18 font-weight-semi-bold d-flex align-items-center justify-content-center'
      style={{ whiteSpace: 'pre-line', color: '#A8ABB7' }}
    >
      {t('no_reported')}
    </div>
  );

  return (
    <div className='align-items-start w-100 h-100'>
      {filteredReportedEvents.length === 0 || !timezone
        ? noReportedEvents
        : filteredReportedEvents
            .sort((ev1, ev2) => {
              return ev2.startDate - ev1.startDate;
            })
            .map((item) => {
              let dischargeItemIndex = dischargeExtraData?.findIndex(
                (dischargeItem) => {
                  return dischargeItem.date === item.startDate;
                },
              );
              let displayAlert = false;
              if (
                item.type === 'discharge' &&
                dischargeExtraData[dischargeItemIndex]?.relationDiff <
                  DISCHARGE_ALERT_THRESHOLD
              ) {
                displayAlert = true;
              }
              let note = item?.note ? t(item.note) : '';
              return (
                <EventRow
                  key={item.type + item.startDate}
                  type={item.type}
                  startDate={item.startDate}
                  value={item.value}
                  extraData={{
                    endDate: item?.endDate,
                    duration: item?.duration,
                    fertType: item?.fertType,
                    wfrValue: item?.wfrValue,
                    wfrEndDate: item?.wfrEndDate,
                    sensorId: item?.sensorId,
                    wtrType: item?.wtrType,
                    pressure: item?.pressure,
                    pressureUnit: item?.pressure_unit,
                    pressureAlert: item?.pressure_alert,
                    wtrConcentrations:
                      selectedFarmingUnit?.wt?.material_concentrations?.hasOwnProperty(
                        item?.wtrType,
                      )
                        ? selectedFarmingUnit?.wt?.material_concentrations[
                            item?.wtrType
                          ]
                        : wtrMaterialConcentrationsDict[item?.type],
                  }}
                  units={item.unit}
                  timezone={timezone}
                  event={'reported'}
                  source={item.source}
                  manufacturer={item?.manufacturer}
                  sensorID={item?.sensor_id}
                  shouldDisplayHover={true}
                  displayAlert={displayAlert}
                  note={note}
                />
              );
            })}
    </div>
  );
});

const EventRow = memo(
  ({
    type,
    startDate,
    value,
    extraData,
    units,
    timezone,
    event,
    source,
    manufacturer,
    sensorID,
    shouldDisplayHover,
    displayAlert,
    note,
  }) => {
    const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
    const [isEventHovered, setIsEventHovered] = useState(false);
    const { t } = useTranslation();
    // Hold last-seen pressure: SET_ALL_DISTRIBUTIONS races with UPDATE_FIELD_HISTORICAL_DATA and drops the field.
    const stablePressureRef = useRef({ value: null, unit: null, alert: null });
    const isReported = event === 'reported';
    let sourceID = '';
    // Reported events hide the source subtitle; the pressure value overrides this.
    let showSourceSubtitle = !isReported;

    if (source === 'manual') {
      sourceID = source;
    } else {
      sourceID = sensorID;
    }
    const item = [
      type,
      source,
      startDate,
      extraData?.wfrValue,
      extraData?.wfrEndDate,
      extraData?.sensorId,
      sourceID,
    ];
    let dischargeEvent = ['discharge'].includes(type);
    let dischargeLessThan12Hours = dayjs
      .unix(startDate)
      .add(12, 'hours')
      .isBefore(dayjs());

    useEffect(() => {
      $(function () {
        $('[data-toggle="tooltip"]').tooltip();
      });
    }, []);

    // 'low' | 'high' | null — set for irrigation events whose pressure fell
    // outside the pressure sensor's configured boundaries.
    let alertLevel = null;

    type = type.replace('_plan', '');
    if (type === 'fertilization' || type === 'plan_fert') {
      type = 'fert';
      source = fertilizerNameToDisplay(extraData.fertType) || source;
    } else if (type === 'wtr' || type === 'plan_wtr') {
      type = 'wtr';
      source =
        planningManager.waterTreatmentDisplayName[extraData.wtrType] || source;
    } else if (type === 'irrigation' || type === 'plan_irr') {
      const isReportedPressureIrrigation =
        type === 'irrigation' &&
        (source === 'high_pressure' || source === 'low_pressure');
      type = 'irr';
      let irrSource = source + manufacturer;
      source = sensorsMap.get(irrSource);
      // Any reported irrigation event may carry a pressure reading. When one
      // exists, show it as a second line (subtitle) — kept visible even for
      // reported events, which otherwise hide the source subtitle. Held in a
      // useRef per row to survive the historical_data race (see README).
      if (extraData?.pressure != null) {
        stablePressureRef.current = {
          value: extraData.pressure,
          unit: extraData.pressureUnit,
          // 'low' | 'high' when the API flagged this irrigation's pressure as
          // outside the sensor's boundaries; null when it's in range.
          alert: pressureAlertLevel(extraData.pressureAlert),
        };
      }
      const stable = stablePressureRef.current;
      alertLevel = stable.alert;
      if (stable.value != null) {
        // Show the pressure value as the subtitle, styled like the source
        // line and kept visible even for reported events. Same formatter the
        // graph popup uses (eventPopUpHelpers) so both read identically.
        source = formatPressureSubtitle(
          stable.value,
          stable.unit,
          t('pressure'),
        );
        showSourceSubtitle = true;
      } else if (isReportedPressureIrrigation) {
        source = source ?? sensorID;
      }
    } else if (type === 'precipitation_daily') {
      type = 'precipitation';
      source = null;
    } else if (type === 'discharge') {
      source = source === 'manual' ? 'Discharge Test' : sensorsMap.get(source);
      value = 'Discharge ' + parseFloat(value)?.toFixed(2);
      units = `(${units})`;
    }

    const onStartHover = () => {
      if (
        shouldDisplayHover &&
        type !== 'precipitation' &&
        (!dischargeEvent || (dischargeEvent && !dischargeLessThan12Hours))
      ) {
        setBackgroundColor('#F6F8FA');
        setIsEventHovered(true);
      }
    };

    const onStopHover = () => {
      if (
        shouldDisplayHover &&
        type !== 'precipitation' &&
        (!dischargeEvent || (dischargeEvent && !dischargeLessThan12Hours))
      ) {
        setBackgroundColor('#FFFFFF');
        setIsEventHovered(false);
      }
    };

    dateTitle = fieldManager.getEventDateTitle(
      startDate,
      extraData?.endDate,
      timezone,
      event,
    );
    dateFormat = dayjs
      .unix(parseInt(startDate))
      .tz(timezone)
      .format('MMM D, hh:mma');
    hourFormat = dayjs.unix(parseInt(startDate)).tz(timezone).format('hh:mma');
    duration = fieldManager.getEventDurationLine(extraData);
    source = source ? source : undefined;
    let durationComponent;
    if (type === 'wtr' || type === 'plan_wtr') {
      if (units) {
        let unitsDisplay = unitDisplayName(units?.toLowerCase());
        durationComponent = (
          <>
            <span className='font-weight-bold text-size-16'>{value}</span>
            <span className='font-weight-normal text-size-16'>
              {unitsDisplay}
            </span>
          </>
        );
        duration = value + unitsDisplay;
        let wtrConcentrations = extraData?.wtrConcentrations
          ? ' ' + extraData?.wtrConcentrations + '%'
          : '';
        value = source + wtrConcentrations;
        source = undefined;
        units = '';
      }
    }

    let valueClassName =
      type === 'plan_note' ? 'text-size-14' : 'font-weight-bold text-size-16';

    let dayTitle =
      dateTitle !== ' ' ? (
        <div
          className='d-flex align-items-start'
          style={{
            gap: '2px',
            paddingTop: '3px',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <div
            className='d-flex justify-content-end bg-grey96 font-weight-semi-bold text-size-14 text-grey45'
            style={{
              padding: '1px 6px',
              borderRadius: '3px',
              alignSelf: 'end',
            }}
          >
            {dateTitle + ' ' + hourFormat}
          </div>
          <div className='text-size-14 text-grey71'>{note}</div>
        </div>
      ) : (
        <div className='font-weight-normal text-size-14 text-grey71'>
          <div className='d-flex justify-content-end'>{dateFormat}</div>
          <div>{note}</div>
        </div>
      );

    const dischargeTooltipContainerID = 'tooltipContainer_' + startDate;
    const dischargeTooltipID = 'tooltipID_' + startDate;
    const pressureAlertTooltipID = 'pressureAlertTooltip_' + startDate;
    // Alert badge on an irrigation record whose pressure breached the sensor's
    // boundaries. Same alert the Notification Center shows (backend cases
    // 26/27), which is styled 'alert' — so it uses the red alert icon, like
    // the discharge-exceeding badge above.
    const pressureAlertBadge = alertLevel ? (
      <img
        data-toggle='tooltip'
        id={pressureAlertTooltipID}
        onMouseOver={() => {
          $(`#${pressureAlertTooltipID}`).tooltip('show');
        }}
        style={{ marginLeft: '4px' }}
        data-placement='top'
        title={t(pressureAlertHintKey(alertLevel))}
        src={require('images/icons/red_alert_icon.svg')}
      />
    ) : null;
    if (dateTitle == '') {
      return <div></div>;
    } else {
      return (
        <div
          onMouseEnter={onStartHover}
          onMouseLeave={onStopHover}
          className='d-flex align-self-stretch flex-grow-0'
          style={{
            height: '64px',
            borderBottom: '1px solid #EEF1F6',
            boxSizing: 'border-box',
            padding: '12px',
            paddingLeft: '16px',
            paddingRight: '16px',
            gap: '10px',
            backgroundColor: backgroundColor,
          }}
        >
          <div
            className='d-flex flex-grow-1 align-items-center'
            style={{ gap: '16px' }}
          >
            <div className='d-flex align-items-center'>
              <img src={IMAGES_MAP[type]} />
            </div>
            <div
              className='d-flex justify-content-center align-items-start '
              style={{ gap: '2px' }}
            >
              {duration.length === 0 ? (
                <div
                  className='text-grey45 d-flex justify-content-start'
                  style={{
                    borderRadius: '5px',
                    height: '42px',
                  }}
                >
                  <div
                    className='overflow-hidden align-self-center'
                    id={dischargeTooltipContainerID}
                    style={{
                      maxHeight: '42px',
                      lineHeight: '20px',
                      width: '188px',
                    }}
                  >
                    <div className='d-flex'>
                      <span className={valueClassName}>{value + ' '}</span>
                      {type !== 'wtr' && (
                        <span className='font-weight-normal text-size-16'>
                          {units}
                        </span>
                      )}
                      {displayAlert && (
                        <img
                          data-toggle='tooltip'
                          data-container={`#${dischargeTooltipContainerID}`}
                          id={dischargeTooltipID}
                          onMouseOver={() => {
                            $(`#${dischargeTooltipID}`).tooltip('show');
                          }}
                          style={{ marginLeft: '4px' }}
                          data-placement='top'
                          title={t('discharge_result_exceeding_hint')}
                          src={require('images/icons/red_alert_icon.svg')}
                        />
                      )}
                      {pressureAlertBadge}
                    </div>
                    {showSourceSubtitle && source && (
                      <div
                        style={{
                          color: 'var(--color-grey-71)',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: 1.3,
                        }}
                      >
                        {source}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className='d-flex w-100'
                  style={{ flexDirection: 'column' }}
                >
                  <div
                    style={{ flexDirection: 'row' }}
                    className='text-grey45 justify-content-center'
                  >
                    <span className='font-weight-bold text-size-16'>
                      {value + ' '}
                    </span>
                    <span className='font-weight-normal text-size-16'>
                      {units + ' / '}
                    </span>
                    {type === 'plan_wtr' || type === 'wtr' ? (
                      durationComponent
                    ) : (
                      <>
                        <span className='font-weight-bold text-size-16'>
                          {duration[0]}
                        </span>
                        <span className='font-weight-normal text-size-16'>
                          {duration[1]}
                        </span>
                        {duration.length > 2 ? (
                          <span className='font-weight-bold text-size-16'>
                            {duration[2]}
                          </span>
                        ) : null}
                        {duration.length > 2 ? (
                          <span className='font-weight-normal text-size-16'>
                            {duration[3]}
                          </span>
                        ) : null}
                      </>
                    )}
                    {pressureAlertBadge}
                  </div>
                  {showSourceSubtitle && source && (
                    <div
                      style={{
                        color: 'var(--color-grey-71)',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: 1,
                      }}
                    >
                      {source}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            className='d-flex p-0 justify-content-end align-items-center'
            style={{ gap: '8px' }}
          >
            {dayTitle}
          </div>

          {/*display the delete icon only for the hovered item*/}
          {isEventHovered &&
          type !== 'precipitation' &&
          (!dischargeEvent || (dischargeEvent && !dischargeLessThan12Hours)) &&
          shouldDisplayHover ? (
            <div
              className={
                'position-absolute d-flex flex-row-reverse align-items-center'
              }
              style={{
                width: '117px',
                right: '12px',
                height: '38px',
                background:
                  'linear-gradient(270deg, #F6F8FA 50%, #F6F8FA 50%, rgba(246, 248, 250, 0) 100%)',
                cursor: 'pointer',
              }}
            >
              <div>
                <div
                  className='mr-3'
                  data-toggle='modal'
                  data-target='#deleteModal'
                  data-values={item}
                >
                  <img src={require('images/icons/trash_icon.svg')} />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      );
    }
  },
);

function NextActionsContainer({
  selectedField,
  timezone,
  parentHeight,
  heigtAuto,
  fieldHasPlan,
  farmLayout,
}) {
  const listContainerRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [reportedEvents, setReportedEvents] = useState([]);
  // Single source of truth for the filter: the unchecked (inactive) event
  // types, kept per tab. Both the checkbox chips and the filtered event lists
  // are derived from this, so they can never disagree (N-182).
  const [inactiveTypes, setInactiveTypes] = useState({
    'coming up': [],
    reported: [],
  });
  const [selectedEventsType, setSelectedEventsType] = useState(
    fieldHasPlan ? 'coming up' : 'reported',
  );
  const [filterModalPosition, setFilterModalPosition] = useState('244px');
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.authentication_data.user_data);
  const selectedDistribution = useSelector(
    (state) => state.distribution_data.selected_entities.distribution,
  );
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );
  const loadHistoryStatus = useSelector(
    (state) => state.distribution_data.field_load_history,
  );
  const { t, i18n } = useTranslation();
  let isArchive = selectedField?.settings?.field_state === 'archived';
  let titleColor = isArchive ? ' text-grey71' : ' text-grey45';

  const EVENT_TYPES_DICTIONARY = useMemo(() => {
    return {
      discharge: t('discharge_test'),
      precipitation_daily: t('rain'),
      plan_irr: t('irrigation'),
      plan_fert: t('fertilization'),
      fertilization: t('fertilization'),
      irrigation: t('irrigation'),
      wtr: t('planner_water_treatment'),
      plan_wtr: t('planner_water_treatment'),
    };
  }, [t]);

  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }

    if (hasReportedHistoryData(selectedField?.historical_data)) {
      let res = fieldManager.historical_data_to_array(
        selectedField.historical_data,
      );
      const filtered = res.filter((x) => x.type != 'precipitation');
      setReportedEvents(filtered);
    } else {
      setReportedEvents([]);
    }
  }, [selectedField?.id, selectedField?.historical_data, loadHistoryStatus]);

  // Switching to a different field clears the filter to all-checked, preserving
  // the previous behavior where filtered state was rebuilt per field.
  useEffect(() => {
    setInactiveTypes({ 'coming up': [], reported: [] });
  }, [selectedField?.id]);

  useEffect(() => {
    const distributionId = selectedDistribution?.id;
    const farmingUnitId = selectedFarmingUnit?.id;
    const fieldId = selectedField?.id;

    if (!distributionId || !farmingUnitId || !fieldId) {
      return;
    }

    dispatch(
      distributionActions.get_field_history(
        distributionId,
        farmingUnitId,
        fieldId,
      ),
    );
  }, [
    selectedDistribution?.id,
    selectedFarmingUnit?.id,
    selectedField?.id,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      setPlans([]);
      setReportedEvents([]);
    };
  }, []);

  const initNextActionEvents = () => {
    planningManager.getPlannerItems(
      selectedField?.id,
      userData,
      handlePlansChange,
    );
  };

  // Records only the selection: derive the inactive (unchecked) types for the
  // active tab. The chips and filtered lists recompute from this via useMemo.
  const setActiveTypesList = useCallback(
    (updatedTypes) => {
      const inactive = getInactiveTypesFromChips(updatedTypes);
      setInactiveTypes((prev) => ({ ...prev, [selectedEventsType]: inactive }));
    },
    [selectedEventsType],
  );

  const handlePlansChange = (plans) => {
    setPlans(plans);
  };

  useEffect(() => {
    setSelectedEventsType(fieldHasPlan ? 'coming up' : 'reported');
  }, [fieldHasPlan]);

  useEffect(() => {
    if (selectedField && fieldHasPlan && userData) {
      initNextActionEvents();
    }
  }, [selectedField?.id, fieldHasPlan, userData]);

  useEffect(() => {
    const updateWindowDimensions = () => {
      const newWidth = window.innerWidth;
      if (newWidth < 1536) {
        setFilterModalPosition('285px');
      } else {
        setFilterModalPosition('244px');
      }
    };

    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  // Filtered lists derived from the per-tab inactive selection. Memoized so the
  // reference stays stable while the selection is unchanged (keeps the
  // ReportedEvents discharge effect from re-running in a loop).
  const filteredReportedEvents = useMemo(
    () =>
      filterEventsByInactiveTypes(
        reportedEvents,
        inactiveTypes.reported,
        'type',
      ),
    [reportedEvents, inactiveTypes.reported],
  );

  const filteredComingUpEvents = useMemo(
    () =>
      filterEventsByInactiveTypes(
        plans,
        inactiveTypes['coming up'],
        'planType',
      ),
    [plans, inactiveTypes['coming up']],
  );

  let nextActionsStyle = heigtAuto
    ? 'overflow-auto d-flex flex-column align-items-start w-100 nextActionsContainer list-group-item p-0'
    : 'overflow-auto d-flex flex-column align-items-start w-100 list-group-item p-0';
  let comingUpListStyle =
    filteredComingUpEvents.length === 0
      ? 'd-flex w-100 h-100 justify-content-center align-items-center'
      : filteredComingUpEvents[filteredComingUpEvents.length - 1].startDate <
          dayjs().unix()
        ? 'd-flex w-100 h-100 justify-content-center align-items-center'
        : 'd-block w-100';
  let reportedListStyle =
    'd-flex w-100 h-100 justify-content-center align-items-center';

  // Checkbox chips derived from the same inactive selection as the lists above,
  // so the two can never disagree.
  const reportedTypes = useMemo(
    () => getReportedTypeChips(reportedEvents, inactiveTypes.reported),
    [reportedEvents, inactiveTypes.reported],
  );

  const comingUpTypes = useMemo(
    () =>
      getComingUpTypeChips(plans, inactiveTypes['coming up'], dayjs().unix()),
    [plans, inactiveTypes['coming up']],
  );

  // The standalone (no-plan) Filter and the tabbed NavigationBar filter show the
  // chips for the active tab; in the no-plan path the tab is always 'reported'.
  const filterMenuTypes = useMemo(() => {
    return selectedEventsType === 'reported' ? reportedTypes : comingUpTypes;
  }, [selectedEventsType, reportedTypes, comingUpTypes]);

  return (
    <div
      style={
        farmLayout
          ? {
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 0',
              minHeight: 0,
            }
          : undefined
      }
    >
      <div
        className={
          farmLayout
            ? 'd-flex justify-content-between align-items-center'
            : 'flex row justify-content-between ml-1 mr-1'
        }
        style={farmLayout ? { height: 24, marginBottom: 4 } : undefined}
      >
        <div className='d-flex'>
          <div className={'text-size-16 font-weight-bold d-flex' + titleColor}>
            {upper(t(fieldHasPlan ? 'events_list' : 'reported'))}
          </div>
          <div
            className={'font-weight-normal'}
            style={{
              position: 'relative',
              display: 'inline-block',
              left: '2px',
              marginTop: '-5px',
            }}
          >
            {!fieldHasPlan && (
              <Filter
                typesList={filterMenuTypes}
                setTypesList={setActiveTypesList}
                typesDictionary={EVENT_TYPES_DICTIONARY}
                event={'reported'}
                style={{ padding: '0px' }}
              />
            )}
          </div>
        </div>

        <div
          type='button'
          className={`d-flex bg-blue-primary align-items-center ${
            farmLayout ? '' : 'mb-2'
          }`}
          style={{ borderRadius: farmLayout ? 4 : 3, height: 24 }}
          data-toggle='modal'
          data-target='#reportEventModal'
        >
          <span
            className={`text-size-14 font-weight-semi-bold text-white ${
              farmLayout ? '' : 'pl-4 pr-4'
            }`}
            style={farmLayout ? { padding: '0 12px' } : undefined}
          >
            {farmLayout ? t('report_event') : upper(t('report_event'))}
          </span>
        </div>
      </div>
      <div
        className='d-flex bg-white'
        style={{
          borderRadius: farmLayout ? '16px' : '5px',
          border: '1px solid #DCE0E8',
          overflowX: 'hidden',
          ...(farmLayout && {
            backgroundColor: 'var(--color-grey-96)',
            overflow: 'hidden',
            flex: '1 1 0',
            minHeight: 0,
          }),
        }}
      >
        {/* In the Farm View the list lives inside the trailing panel's
            details box (position: relative; z-index: 0), a stacking context
            that would keep the modal under Bootstrap's body-level backdrop.
            Render the modals at the body so they overlay the whole page. */}
        {farmLayout ? (
          createPortal(
            <>
              <ReportEventModal t={t} i18n={i18n} />
              <DeleteModal t={t} />
            </>,
            document.body,
          )
        ) : (
          <>
            <ReportEventModal t={t} i18n={i18n} />
            <DeleteModal t={t} />
          </>
        )}
        <div
          ref={listContainerRef}
          className={nextActionsStyle}
          style={{
            height: farmLayout ? '100%' : parentHeight,
            border: 'none',
            zIndex: 1,
          }}
        >
          {fieldHasPlan && (
            <NavigationBar
              typesDictionary={EVENT_TYPES_DICTIONARY}
              setTypesList={setActiveTypesList}
              setEvent={setSelectedEventsType}
              event={selectedEventsType}
              choices={choices}
              filterMenuTypes={filterMenuTypes}
            />
          )}
          {fieldHasPlan && (
            <div
              style={{ overflowY: 'scroll' }}
              className={
                selectedEventsType === 'coming up'
                  ? comingUpListStyle
                  : 'd-none'
              }
            >
              <ComingUp plans={filteredComingUpEvents} timezone={timezone} />
            </div>
          )}
          <div
            className={
              selectedEventsType === 'reported' ? reportedListStyle : 'd-none'
            }
          >
            <ReportedEvents
              filteredReportedEvents={filteredReportedEvents}
              timezone={timezone}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

module.exports = withTranslation()(NextActionsContainer);
